'use client';
/* reformat pass: Why-Locobuzz section removed; responsive split-stacking added. */

import { useState, useRef, useCallback, useEffect } from 'react';
import { motion, AnimatePresence, useScroll, useMotionValueEvent, useTransform, useInView, useMotionValue, animate } from 'framer-motion';
import Link from 'next/link';
import { BarChart3, Radio, TrendingUp, Users, Megaphone, Globe, Bell, Clock, LayoutDashboard, Send, Tv, ShieldCheck, ChevronDown, ArrowRight, ArrowUpRight, Zap, Search, Target, FileBarChart, Eye, Lightbulb, Sparkles, Check, MapPin } from 'lucide-react';
import BookDemoSection from '@/components/BookDemoSection';
import { BookDemoButton, FaqAccordion } from '@/components/ui';
import { ScrollRevealText } from '@/components/primitives/ScrollRevealText';
import { CapabilityBento } from '@/components/primitives/CapabilityBento';
import { fadeUp as mfadeUp, staggerContainer, viewportOnce, spring, springSoft } from '@/lib/motion';
import styles from './page.module.css';

/* Trusted-brand logos for the marquee strip (files live in /public/clients). */
const CLIENTS = [
  { src: '/clients/au-small-finance-bank.png', alt: 'AU Small Finance Bank' },
  { src: '/clients/bandhan-bank.png', alt: 'Bandhan Bank' },
  { src: '/clients/tata-capital.png', alt: 'Tata Capital' },
  { src: '/clients/poonawalla-fincorp.avif', alt: 'Poonawalla Fincorp' },
  { src: '/clients/lendenclub.png', alt: 'LenDenClub' },
  { src: '/clients/instamoney.png', alt: 'InstaMoney' },
  { src: '/clients/razorpay.svg', alt: 'Razorpay' },
  { src: '/clients/protean.png', alt: 'Protean' },
  { src: '/clients/medibuddy.png', alt: 'MediBuddy' },
  { src: '/clients/gleneagles-hospitals.png', alt: 'Gleneagles Hospitals' },
  { src: '/clients/licious.png', alt: 'Licious' },
  { src: '/clients/urban-company.png', alt: 'Urban Company' },
  { src: '/clients/hathway.webp', alt: 'Hathway' },
  { src: '/clients/realme.png', alt: 'realme' },
  { src: '/clients/tecno.png', alt: 'Tecno' },
  { src: '/clients/crompton.png', alt: 'Crompton' },
  { src: '/clients/hafele.jpg', alt: 'Häfele' },
  { src: '/clients/senco.png', alt: 'Senco Gold & Diamonds' },
  { src: '/clients/blackberrys.avif', alt: 'Blackberrys' },
  { src: '/clients/matrimony.png', alt: 'Matrimony' },
];

/* Headline outcome numbers for the count-up Results band. Numeric targets so
   they can animate from 0 → value when scrolled into view. */
const RESULTS = [
  { target: 85, suffix: '%', prefix: '', label: 'Response rate within 3 months', foot: 'up from 39% at onboarding' },
  { target: 15, suffix: ' min', prefix: '', label: 'Average response time', foot: 'down from 24 hours' },
  { target: 1, suffix: 'B+', prefix: '', label: 'Signals processed / day', foot: 'across every channel' },
  { target: 95, suffix: '%', prefix: '', label: 'Enterprise retention', foot: '350+ brands and governments' },
];

/* ── DATA — content preserved verbatim from the original page ───── */
import { useTranslation } from "@/hooks/useTranslation";
const STATS = [{
  n: '39% → 85%',
  label: 'Response rate lift in 3 months. Real deployment. Real number.'
}, {
  n: '15 min',
  label: 'Average response time, down from 24 hours.'
}, {
  n: '1B+',
  label: 'Signals/day processed across all channels. No blind spots.'
}, {
  n: '350+',
  label: 'Enterprise and government brands. 95% retention.'
}];
const CAPABILITIES = [{
  Icon: BarChart3,
  color: 'var(--brand)',
  colorLight: 'var(--brand-subtle)',
  colorBorder: 'var(--brand-border)',
  title: 'Social Media Analytics',
  sub: 'Stop reading posts. Start reading intent.',
  desc: 'Goes beyond keyword tracking to detect emotion, urgency, and audience mood so you know not just what is being said, but why it matters.',
  bullets: ['Aspect-level sentiment analysis across 30+ languages with ContextualPulse™', 'Detect tone, sarcasm, appreciation, complaints, and purchase signals in real time', 'Segment audiences by mood, loyalty stage, and conversion potential', 'Surface emerging conversations before they trend and act while the window is open']
}, {
  Icon: Globe,
  color: 'var(--brand)',
  colorLight: 'var(--brand-subtle)',
  colorBorder: 'var(--brand-border)',
  title: 'Brand Monitoring',
  sub: 'Multiple channels, zero blind spots.',
  desc: 'Your audience does not stick to one platform. Neither does Locobuzz. Social, reviews, web, and beyond.',
  bullets: ['Social: Facebook, Instagram, X (Twitter), LinkedIn, YouTube, TikTok', 'Reviews: Google My Business, Google Play, App Store, Amazon, Flipkart, Glassdoor', 'Web: News, blogs, Quora, forums, complaint sites', 'Competitor brand monitoring across the same channels at the same depth in real time']
}, {
  Icon: TrendingUp,
  color: 'var(--brand)',
  colorLight: 'var(--brand-subtle)',
  colorBorder: 'var(--brand-border)',
  title: 'Competitive Benchmarking',
  sub: 'Your competitors are talking to your customers.',
  desc: 'Locobuzz tells you exactly what they are saying, how audiences are responding, and where you have room to take share.',
  bullets: ['Track Share of Voice, sentiment scores, and engagement rates vs. named competitors', 'Compare campaign messaging effectiveness and audience response side by side', 'Spot whitespace opportunities competitors have not claimed yet', 'PR benchmarking: track news coverage, top authors, and sentiment vs. rivals']
}, {
  Icon: Target,
  color: 'var(--brand)',
  colorLight: 'var(--brand-subtle)',
  colorBorder: 'var(--brand-border)',
  title: 'Campaign Performance Tracking',
  sub: 'Impressions do not pay salaries.',
  desc: 'Locobuzz tracks what your campaigns actually delivered: sentiment shifts, audience growth, influencer impact, and ROI — not just reach.',
  bullets: ['Full-funnel campaign analytics: engagement, sentiment, share of voice, influencer activity', 'Paid vs. organic performance via native Facebook and X ad account integration', 'Identify top-performing content, best posting windows, and highest-impact influencers', 'Intent Labels auto-classify every campaign mention: appreciation, complaint, query, suggestion']
}, {
  Icon: Users,
  color: 'var(--brand)',
  colorLight: 'var(--brand-subtle)',
  colorBorder: 'var(--brand-border)',
  title: 'Real-Time Audience Insights',
  sub: 'Forget personas built from surveys taken six months ago.',
  desc: 'Locobuzz gives you a live, granular profile of your audience built from what they are actually saying right now.',
  bullets: ['Audience profiling by profession, interests, geography, and behavioral patterns', 'Segment by engagement level, loyalty signals, and conversion readiness', 'Understand how different audience segments respond to different content formats and tones']
}, {
  Icon: Megaphone,
  color: 'var(--brand)',
  colorLight: 'var(--brand-subtle)',
  colorBorder: 'var(--brand-border)',
  title: 'Influencer Tracking',
  sub: 'Every influencer partnership should come with a ROI number.',
  desc: 'Locobuzz makes that possible. Measure impact, not follower counts.',
  bullets: ['Track sentiment and engagement generated by every influencer mentioning your brand', 'Measure direct impact on brand performance metrics and campaign reach', 'Identify which influencers are driving your competitors audiences and build a counter-strategy']
}, {
  Icon: Search,
  color: 'var(--brand)',
  colorLight: 'var(--brand-subtle)',
  colorBorder: 'var(--brand-border)',
  title: 'Market Research',
  sub: 'Your audience is running focus groups 24/7.',
  desc: 'Real consumers are sharing real opinions about your category every hour. Locobuzz captures it and structures it into actionable research.',
  bullets: ['Extract product feedback, unmet needs, and buying triggers from live conversations', 'Identify trends before they hit mainstream — build campaigns that feel ahead of the curve', 'Feed insights directly into GTM strategy, brand positioning, and product roadmaps']
}, {
  Icon: FileBarChart,
  color: 'var(--brand)',
  colorLight: 'var(--brand-subtle)',
  colorBorder: 'var(--brand-border)',
  title: 'PR Dashboard',
  sub: 'Own your narrative, or someone else will.',
  desc: 'Track every mention in earned media. Understand what is being amplified, by whom, and whether it is helping or hurting.',
  bullets: ['Monitor news coverage, top domains, and influential authors amplifying your brand', 'Sentiment analysis on earned media vs. competitors: positive, negative, neutral', 'Identify PR opportunities where competitor coverage is weak']
}, {
  Icon: Bell,
  color: 'var(--brand)',
  colorLight: 'var(--brand-subtle)',
  colorBorder: 'var(--brand-border)',
  title: 'Real-Time Alerts',
  sub: 'A viral negative thread moves faster than your weekly report cycle.',
  desc: 'Locobuzz sends alerts the moment something needs your attention — not the moment it is already out of hand.',
  bullets: ['Keyword, channel, influencer, sentiment, and virality alerts in real time', 'Configure frequency: real-time, hourly, or daily per use case', 'Auto-tagging sorts every mention into 7 intent buckets so your team knows what to action first']
}, {
  Icon: Clock,
  color: 'var(--brand)',
  colorLight: 'var(--brand-subtle)',
  colorBorder: 'var(--brand-border)',
  title: 'Historic Listening',
  sub: 'Context your competitors cannot buy.',
  desc: 'Access brand data from before your Locobuzz onboarding via configured channels or keywords. Benchmark current performance against the past without losing context.',
  bullets: ['Retrieve historical brand data before onboarding date', 'Benchmark current performance against past baselines', 'Understand long-term sentiment and conversation trends']
}];
const DASHBOARD_FEATURES = [{
  Icon: LayoutDashboard,
  title: '30+ Pre-Built Dashboards',
  desc: 'One for every channel — Facebook, Instagram, LinkedIn, X, YouTube, TikTok, App Stores, eCommerce, GMB, Glassdoor, Quora. Live from day one.'
}, {
  Icon: Zap,
  title: 'DIY Dashboard Builder',
  desc: 'Drag, drop, filter. Build the exact view your CMO needs for the next board meeting — no data team required.'
}, {
  Icon: BarChart3,
  title: 'Custom Widget Maker',
  desc: 'Build bespoke charts from your own data points. Filter by sentiment, channel, time frame, or campaign. Save to dashboard.'
}, {
  Icon: Send,
  title: 'Scheduled Reports',
  desc: 'Deliver PDFs and PPTs to your team\'s inbox automatically. Daily, weekly, monthly — your cadence, your format.'
}];
const PUBLISHING_FEATURES = [{
  Icon: Globe,
  title: 'All Channels, One Workspace',
  desc: 'Facebook, Instagram (Feed, Reels, Stories, Carousels), LinkedIn, X, YouTube, and GMB — scheduled and published from one content hub.'
}, {
  Icon: LayoutDashboard,
  title: 'Content Calendar',
  desc: 'Day, week, and month views. Filter by post labels. Spot gaps before they become missed opportunities.'
}, {
  Icon: Eye,
  title: 'Approval Workflows',
  desc: 'Maker-checker built in. No post goes live without the right eyes on it.'
}, {
  Icon: Megaphone,
  title: 'Hyperlocal Publishing',
  desc: '100 locations? 1,000? Publish one post with personalisation variables and hit them all at once. GMB-supported.'
}];
const COMMAND_FEATURES = [{
  Icon: BarChart3,
  title: 'Live Brand Health & ROI',
  desc: 'Share of Voice, campaign ROI, and brand health on one screen — updated in real time.'
}, {
  Icon: Bell,
  title: 'SignalSense™ Early Warning',
  desc: 'Early-warning for crisis signals, viral threats, and reputation risks before they escalate.'
}, {
  Icon: TrendingUp,
  title: 'Real-Time Competitive Intelligence',
  desc: 'Who is moving, what is resonating, where you are losing ground — all visible, all live.'
}, {
  Icon: Radio,
  title: 'Deviation Alerts & Network Analysis',
  desc: 'Alerts when conversation volume, sentiment, or channel patterns shift significantly. Understand how trends propagate and where to intercept.'
}];
const WHY_US = [{
  Icon: Lightbulb,
  title: 'AI-Native',
  desc: 'ContextualPulse™, SignalSense™, ResponseGenie™, AgentIQ™, Location Intelligence, Workflow Automation — six proprietary engines trained on a decade of enterprise data. Not a thin wrapper over GPT-4.'
}, {
  Icon: Zap,
  title: 'Unified',
  desc: 'Most stacks force a choice. Locobuzz is the only enterprise platform where your listening, analytics, and publishing workflows share one data model.'
}, {
  Icon: Globe,
  title: 'Coverage',
  desc: 'Social, reviews, news, forums, app stores, eCommerce, and location platforms. Multiple channels. No blind spots.'
}, {
  Icon: Target,
  title: 'Efficiency',
  desc: 'One contract. One workspace. One source of truth. Measured in 30 to 40% lower total cost of ownership vs. the typical stack.'
}, {
  Icon: Users,
  title: 'Trusted',
  desc: 'IndiGo, Federal Bank, Hyundai, Apollo Hospitals, Tata Consumer, Maruti Suzuki. 95% retention. Because outcomes show up every quarter.'
}, {
  Icon: ShieldCheck,
  title: 'Security',
  desc: 'ISO 27001:2022 · SOC 2 Type II · GDPR · DPDP Act 2023 · CERT-In ready. Enterprise-scale security that regulated industries need.'
}];
const FAQS = [{
  q: 'What does Locobuzz do for marketing teams?',
  a: 'It gives your team a live intelligence layer — social media analytics, competitive benchmarking, campaign performance tracking, audience insights, and publishing — all in one platform. So you stop working from reports and start working from real-time signals.'
}, {
  q: 'Which channels does it monitor?',
  a: 'Facebook, Instagram, X (Twitter), LinkedIn, YouTube, TikTok, Google My Business, Apple App Store, Google Play Store, Amazon, Flipkart, Glassdoor, Quora, news publications, blogs, and discussion forums. Plus competitor tracking across the same set.'
}, {
  q: 'How does competitive benchmarking work?',
  a: 'Locobuzz tracks your rivals Share of Voice, sentiment, campaign messaging, influencer activity, and PR coverage — in real time, across every channel. You get a live scorecard, not a quarterly slide deck.'
}, {
  q: 'What is ContextualPulse™?',
  a: "It's Locobuzz's proprietary AI engine for sentiment analysis. It goes beyond keywords to detect emotion, urgency, sarcasm, and intent — with 99% accuracy across 30+ languages. It tells you why customers feel what they feel, not just that they feel it."
}, {
  q: 'Can it handle brands with hundreds of locations?',
  a: 'Yes. Hyperlocal Profile Management lets you publish personalised content to hundreds of locations simultaneously and monitor each location\'s reputation and reviews from a single dashboard.'
}, {
  q: 'How quickly can my team go live?',
  a: 'Live in under 30 days. Measurable outcomes in 90. A dedicated CSM, a 4-week implementation playbook, and 350+ deployments of muscle memory mean we know exactly when each milestone lands.'
}];

/* ── Reusable framer-motion variants ─────────────────────────────── */
const fadeUp = {
  hidden: {
    opacity: 0,
    y: 24
  },
  show: {
    opacity: 1,
    y: 0,
    transition: {
      type: 'spring',
      damping: 18,
      stiffness: 110,
      mass: 0.9
    }
  }
};
const stagger = {
  hidden: {},
  show: {
    transition: {
      staggerChildren: 0.08
    }
  }
};

/* ── Brand palette shortcuts ─────────────────────────────────────── */
const BRAND = 'var(--brand)';
const BRAND_DEEP = 'var(--brand-deep)';
const BRAND_SUB = 'var(--brand-subtle)';
const BRAND_BORDER = 'var(--brand-border)';
const TXT_PRIMARY = 'var(--text-primary)';
const TXT_BODY = 'var(--text-body)';
const TXT_MUTED = 'var(--text-muted)';
const BORDER_SOFT = 'rgba(15, 23, 42, 0.08)';
const BORDER_MED = 'rgba(15, 23, 42, 0.12)';

/* ── PAGE ─────────────────────────────────────────────────────── */
export default function MarketingTeamsPageClient() {
  const {
    t
  } = useTranslation();
  /* Keyframes + page-shell responsive rules live in page.module.css. */
  return <div className={styles.pageShell}>
      <Hero />
      <ResultsCountUp />
      <Capabilities />
      <DashboardsAndReporting />
      <Publishing />
      <CommandCentre />
      <FAQSection />
      <ClosingCTA />
      <BookDemoSection />
    </div>;
}

/* ── HERO (redesigned) — asymmetric split: left copy + CTAs, right
   animated "live intelligence" panel. Mesh-gradient background drifts
   slowly; signal chips bob; live dot pulses. ── */
function Hero() {
  const { t } = useTranslation();
  return (
    <section className={styles.hero} style={{ position: 'relative', overflow: 'hidden' }}>
      {/* Animated mesh-gradient background */}
      <div aria-hidden style={{ position: 'absolute', inset: 0, pointerEvents: 'none', overflow: 'hidden' }}>
        <div className="mkt-float" style={{
          position: 'absolute', top: '-18%', left: '-8%', width: 620, height: 620, borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(0,152,255,0.18), transparent 65%)',
          filter: 'blur(20px)', animation: 'mktMeshDrift 18s ease-in-out infinite',
        }} />
        <div className="mkt-float" style={{
          position: 'absolute', top: '4%', right: '-12%', width: 560, height: 560, borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(127,179,255,0.20), transparent 60%)',
          filter: 'blur(20px)', animation: 'mktMeshDrift 22s ease-in-out infinite reverse',
        }} />
        <div style={{
          position: 'absolute', inset: 0,
          backgroundImage: 'radial-gradient(rgba(0,152,255,0.06) 1px, transparent 1px)',
          backgroundSize: '30px 30px', maskImage: 'linear-gradient(180deg, #000 0%, transparent 80%)',
        }} />
      </div>

      <div className="container" style={{ position: 'relative', zIndex: 1 }}>
        <div className="mkt-hero-grid" style={{
          display: 'grid', gridTemplateColumns: '1.1fr 0.9fr', gap: 56, alignItems: 'center',
        }}>
          {/* LEFT — copy */}
          <motion.div variants={staggerContainer(0.1)} initial="hidden" animate="show">
            <motion.span variants={mfadeUp} style={{
              display: 'inline-flex', alignItems: 'center', gap: 8, background: 'var(--brand-subtle)',
              border: '1px solid var(--brand-border)', padding: '6px 14px', borderRadius: 999,
              fontSize: 12, fontWeight: 700, color: 'var(--brand)', letterSpacing: '0.06em',
            }}>
              <Sparkles size={13} strokeWidth={2.5} /> {t("team.marketing")}
            </motion.span>

            <motion.h1 variants={mfadeUp} style={{
              fontWeight: 800, fontSize: 'clamp(34px, 4.6vw, 62px)', lineHeight: 1.08,
              letterSpacing: '-0.03em', color: 'var(--text-primary)', margin: '20px 0 0',
            }}>
              {t("solutions_for_marketing_teams.turn_every_brand_signal_into_a")}{' '}
              <span className="serif-accent italic" style={{ color: 'var(--brand)' }}>
                {t("solutions_for_marketing_teams.campaign_decision")}
              </span>.{' '}
              <span style={{ color: 'var(--brand-deep)' }}>
                {t("solutions_for_marketing_teams.before_your_competitors_do")}
              </span>
            </motion.h1>

            <motion.p variants={mfadeUp} style={{
              fontSize: 18, lineHeight: 1.65, color: 'var(--text-body)', margin: '22px 0 0', maxWidth: 520,
            }}>
              {t("solutions_for_marketing_teams.most_marketing_teams_are_working_wi")}{' '}
              <strong style={{ color: 'var(--brand)', fontWeight: 700 }}>{t("solutions_for_marketing_teams.yesterdays")}</strong>{' '}
              {t("solutions_for_marketing_teams.data")} {t("solutions_for_marketing_teams.locobuzz_changes_that")}
            </motion.p>

            <motion.div variants={mfadeUp} style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginTop: 30 }}>
              <BookDemoButton label={t("hero.cta_book_demo")} />
              <Link href="#capabilities" style={{
                display: 'inline-flex', alignItems: 'center', gap: 8, padding: '13px 22px', borderRadius: 999,
                border: '1px solid var(--brand-border)', background: 'var(--surface-card)', color: 'var(--brand-deep)',
                fontWeight: 700, fontSize: 14, textDecoration: 'none',
              }}>
                {t("solutions_for_marketing_teams.capabilities")} <ArrowRight size={15} />
              </Link>
            </motion.div>

            <motion.div variants={mfadeUp} style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 28 }}>
              {['ISO 27001:2022', 'SOC 2 Type II', 'GDPR', 'CERT-In', 'DPDP 2023'].map(b => (
                <span key={b} style={{
                  display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11, fontWeight: 600,
                  color: 'var(--text-muted)', border: '1px solid rgba(15,23,42,0.08)', borderRadius: 8, padding: '4px 9px',
                }}>
                  <ShieldCheck size={11} color="var(--brand)" />{b}
                </span>
              ))}
            </motion.div>
          </motion.div>

          {/* RIGHT — animated live-intelligence panel */}
          <motion.div className="mkt-hero-panel"
            initial={{ opacity: 0, y: 30, scale: 0.96 }} animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ type: 'spring', damping: 16, stiffness: 90, delay: 0.2 }}
            style={{ position: 'relative' }}
          >
            <div style={{
              background: 'var(--brand-deep)', borderRadius: 24, padding: 22, overflow: 'hidden',
              border: '1px solid rgba(255,255,255,0.10)', boxShadow: 'var(--shadow-panel)',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginBottom: 18 }}>
                <Tv size={15} color="rgba(255,255,255,0.5)" />
                <span style={{ fontSize: 11, fontWeight: 700, color: 'rgba(255,255,255,0.7)', letterSpacing: '0.05em' }}>
                  {t("solutions_for_marketing_teams.command_centre_live_view")}
                </span>
                <span style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 6,
                  fontSize: 10, fontWeight: 800, color: 'var(--brand-sky)', background: 'rgba(127,179,255,0.16)',
                  border: '1px solid rgba(127,179,255,0.3)', padding: '3px 10px', borderRadius: 999 }}>
                  <span style={{ width: 5, height: 5, borderRadius: '50%', background: 'var(--brand-sky)', display: 'block', animation: 'mktPulse 1.6s infinite' }} />
                  {t("solutions_for_marketing_teams.live")}
                </span>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                {[{ label: 'Share of Voice', val: '62.4%', d: '↑ 8.2' }, { label: 'Brand Sentiment', val: '84', d: '↑ 11' }].map((m, i) => (
                  <motion.div key={i} className="mkt-float" style={{
                    padding: '16px', borderRadius: 14, background: 'rgba(255,255,255,0.05)',
                    border: '1px solid rgba(255,255,255,0.09)', animation: `mktFloatChip ${4 + i}s ease-in-out ${i * 0.5}s infinite`,
                  }}>
                    <div style={{ fontSize: 10, fontWeight: 800, color: 'rgba(255,255,255,0.45)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>{t(m.label)}</div>
                    <div style={{ fontSize: 28, fontWeight: 800, color: 'var(--text-inverse)', letterSpacing: '-0.03em', marginTop: 6, lineHeight: 1 }}>{m.val}</div>
                    <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--brand-sky)', marginTop: 5 }}>{m.d}</div>
                  </motion.div>
                ))}
              </div>

              <div style={{
                marginTop: 12, padding: '14px 16px', borderRadius: 14,
                background: 'linear-gradient(135deg, rgba(127,179,255,0.16), rgba(127,179,255,0.05))',
                border: '1px solid rgba(127,179,255,0.3)',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                  <span style={{ fontSize: 9.5, fontWeight: 800, background: 'var(--brand-sky)', color: 'var(--brand-deep)', padding: '2px 8px', borderRadius: 5 }}>
                    {t("solutions_for_marketing_teams.signalsense")}
                  </span>
                  <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--brand-sky)' }}>{t("solutions_for_marketing_teams.viral_thread_detected")}</span>
                </div>
                <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.78)', lineHeight: 1.5 }}>
                  {t("solutions_for_marketing_teams.instagram_32k_mentions_in_2h_action")}
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

/* ── WHITE FOLD — scroll-reveal paragraph (Ultron-style) ────────── */
function ScrollRevealFold() {
  const {
    t
  } = useTranslation();
  return <section style={{
    background: 'var(--surface-card)',
    padding: 'var(--section-pad)',
    position: 'relative'
  }}>
      <div className="container">
        <div style={{
        display: 'grid',
        gridTemplateColumns: '1fr 3fr',
        gap: 56,
        alignItems: 'start'
      }}>
          {/* Left — small kicker label */}
          <div style={{
          position: 'sticky',
          top: 140,
          paddingTop: 14
        }}>
            <span style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 8,
            fontSize: 12,
            fontWeight: 700,
            color: TXT_MUTED,
            letterSpacing: '0.12em',
            textTransform: 'uppercase'
          }}>
              <span style={{
              width: 6,
              height: 6,
              borderRadius: '50%',
              background: BRAND,
              display: 'block'
            }} />
              {t("solutions_for_marketing_teams.why_locobuzz")}
            </span>
          </div>

          {/* Right — paragraph that fills word-by-word on scroll */}
          <ScrollRevealText mutedColor="rgba(15, 23, 42, 0.18)" revealColor={TXT_PRIMARY} range={['start 0.85', 'start 0.2']} style={{
          fontFamily: 'var(--font-geist), system-ui, sans-serif',
          fontSize: 'clamp(22px, 2.5vw, 32px)',
          fontWeight: 600,
          lineHeight: 1.4,
          letterSpacing: '-0.015em',
          margin: 0,
          maxWidth: 880
        }}>
            {`It's the AI marketing intelligence platform that gives your team a live feed of what consumers are saying, feeling, and doing — and converts it into decisions you can act on today.`}
          </ScrollRevealText>
        </div>
      </div>
    </section>;
}

/* (StatsBar removed — metrics now live inside Hero via SlantMetrics.) */

/* ── CAPABILITIES — pinned, two-column scroll-through stage.
   A tall track (capSection) + a sticky inner (capSticky) pin a full-height
   two-column stage. As the user scrolls, the section's on-screen position maps
   to BOTH the active tab (0..9) AND a 0→1 progress value. LEFT column holds the
   always-visible title + the active spotlight (number / title / one-liner) + a
   progress bar + the tab rail. RIGHT column holds the CapabilityBento, sized to
   fill the column height; it updates IN PLACE (no re-mount) so the scroll-
   through stays smooth. Clicking a rail tab smooth-scrolls to that tab.
   On ≤900px the pin is disabled, the columns stack, and tabs are tap-driven
   (see the @media block in page.module.css).
   ── */
function Capabilities() {
  const {
    t
  } = useTranslation();
  const [active, setActive] = useState(0);
  const [progress, setProgress] = useState(0);
  const total = CAPABILITIES.length;
  const cap = CAPABILITIES[active];
  const containerRef = useRef(null);

  /* Active tab + scroll progress from the section's real on-screen position.
     CSS pins the stage (sticky), so the section "freezes" and each scroll step
     advances the tab; past the last tab the page scrolls on, before the first
     it scrolls back up.

     The site's Lenis smooth-scroll moves the page with a CSS transform, so
     window.scrollY stays 0 and native scroll events never fire — that breaks
     framer's useScroll AND scroll listeners. getBoundingClientRect() is the
     only reliable read, so we poll it each animation frame, gated by an
     IntersectionObserver so the loop runs only while the section is in view.
     Disabled ≤900px (section is height:auto there; tabs are tap-driven). */
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return undefined;
    let rafId = 0;
    let lastIdx = -1;
    let lastProg = -1;
    const tick = () => {
      const denom = el.offsetHeight - window.innerHeight;
      if (denom > 0 && !window.matchMedia('(max-width: 900px)').matches) {
        const p = Math.min(1, Math.max(0, -el.getBoundingClientRect().top / denom));
        const idx = Math.min(total - 1, Math.max(0, Math.floor(p * total)));
        if (idx !== lastIdx) { lastIdx = idx; setActive(idx); }
        // Throttle progress updates to meaningful deltas to avoid churn.
        if (Math.abs(p - lastProg) > 0.002) { lastProg = p; setProgress(p); }
      }
      rafId = requestAnimationFrame(tick);
    };
    // Only poll while the section is on/near screen — idles cleanly otherwise.
    const io = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        if (!rafId) rafId = requestAnimationFrame(tick);
      } else if (rafId) {
        cancelAnimationFrame(rafId);
        rafId = 0;
      }
    }, { rootMargin: '120px' });
    io.observe(el);
    return () => { io.disconnect(); if (rafId) cancelAnimationFrame(rafId); };
  }, [total]);

  /* Click a tab → smooth-scroll so its slice of the track is centred (the
     scroll handler then sets it active). */
  const goTo = useCallback(i => {
    if (typeof window !== 'undefined' && window.matchMedia('(max-width: 900px)').matches) { setActive(i); return; }
    const el = containerRef.current;
    if (!el) return;
    const totalScroll = el.offsetHeight - window.innerHeight;
    const targetY = el.offsetTop + ((i + 0.5) / total) * totalScroll;
    // Lenis owns scrolling; native window.scrollTo is a no-op while it's active.
    if (window.lenis?.scrollTo) window.lenis.scrollTo(targetY);
    else window.scrollTo({ top: targetY, behavior: 'smooth' });
  }, [total]);

  /* Bar fill: prefer the continuous scroll progress (smooth), but never let it
     read lower than the active tab's share so the bar and the NN/10 stay in
     sync even at the static endpoints. */
  const barPct = Math.max(progress, (active + 1) / total) * 100;

  return <section id="capabilities" ref={containerRef} className={styles.capSection}>
      <div aria-hidden className={styles.capDots} />

      {/* Two-column stage pins together so the title stays on screen the whole
          time the tabs advance. */}
      <div className={styles.capSticky}>
        <div className={`container ${styles.capInner}`}>
          {/* Full-width header row — sits across the top, above the two columns */}
          <div className={styles.capHeaderBlock}>
            <SectionPill>Capabilities</SectionPill>
            <h2 className={styles.capTitle}>{t("solutions_for_marketing_teams.the_marketing_analytics_dashboard_y")}</h2>
            <p className={styles.capSub}>Everything from social listening to competitive intelligence — unified in one real-time platform.</p>
          </div>

          <div className={styles.capStage}>

            {/* ── LEFT column — spotlight + progress + rail ───── */}
            <div className={styles.capLeft}>
              {/* Active capability spotlight — animates the swap on change */}
              <div className={styles.capSpotlight}>
                {/* Keyed crossfade — entering/exiting children overlap (no
                    mode="wait"), so the swap never blocks on the old child's
                    exit transition completing. Old child fades/slides out while
                    the new one slides up + fades in (~0.3s spring). */}
                <AnimatePresence initial={false}>
                  <motion.div
                    key={active}
                    initial={{ opacity: 0, y: 18 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -14 }}
                    transition={{ type: 'spring', damping: 20, stiffness: 220, mass: 0.7 }}
                    className={styles.capSpotInner}
                  >
                    <span className={styles.capSpotNum}>{String(active + 1).padStart(2, '0')}</span>
                    <h3 className={styles.capSpotTitle}>{t(cap.title)}</h3>
                    <p className={styles.capSpotSub}>{cap.sub}</p>
                  </motion.div>
                </AnimatePresence>
              </div>

              {/* Progress: NN / 10 + thin fill bar */}
              <div className={styles.capProgress}>
                <div className={styles.capProgressMeta}>
                  <span className={styles.capPagCurrent}>{String(active + 1).padStart(2, '0')}</span>
                  <span className={styles.capPagDivider}>/</span>
                  <span className={styles.capPagTotal}>{total}</span>
                </div>
                <div className={styles.capBarTrack} role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(barPct)}>
                  <motion.div
                    className={styles.capBarFill}
                    animate={{ width: `${barPct}%` }}
                    transition={{ type: 'spring', damping: 26, stiffness: 180, mass: 0.6 }}
                  />
                </div>
              </div>

              {/* Tab rail — compact highlighted progress list */}
              <nav aria-label={t("solutions_for_marketing_teams.capabilities")} className={styles.capRail}>
                {CAPABILITIES.map((c, i) => {
                const isActive = i === active;
                const isDone = i < active;
                return <button key={c.title} type="button" onClick={() => goTo(i)} aria-current={isActive ? 'true' : undefined} className={`${styles.capRailItem} ${isActive ? styles.capRailItemActive : ''} ${isDone ? styles.capRailItemDone : ''}`}>
                      <span className={styles.capRailNum}>{String(i + 1).padStart(2, '0')}</span>
                      <span className={styles.capRailLabel}>{t(c.title)}</span>
                    </button>;
              })}
              </nav>
            </div>

            {/* ── RIGHT column — bento fills the available height ──────── */}
            <div className={styles.capRight}>
              {/* No key — the bento updates in place as you scroll instead of
                  re-mounting + re-running its entrance animation each tab (which
                  made the scroll-through feel janky). A light crossfade keys the
                  content swap only. */}
              <CapabilityBento capability={cap} deckIndex={active} />
            </div>

          </div>
        </div>
      </div>

    </section>;
}

/* ── DASHBOARDS & REPORTING ────────────────────────────────────── */
function DashboardsAndReporting() {
  const {
    t
  } = useTranslation();
  return <section id="dashboards" style={{
    padding: 'var(--section-pad)',
    background: 'var(--surface-card)'
  }}>
      <div className="container">
        <div style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1.1fr',
        gap: 72,
        alignItems: 'start'
      }}>
          <motion.div initial="hidden" whileInView="show" viewport={{
          once: true
        }} variants={fadeUp}>
            <SectionPill>{t("solutions_for_marketing_teams.dashboards_reporting")}</SectionPill>
            <h2 style={sectionTitleStyle}>{t("solutions_for_marketing_teams.customizable_dashboards_200_widgets")}</h2>
            <p style={{
            fontSize: 17,
            color: TXT_BODY,
            lineHeight: 1.7,
            marginTop: 14,
            maxWidth: 460
          }}>
              {t("solutions_for_marketing_teams.your_team_should_be_able_to_build_f")}
            </p>
          </motion.div>

          <motion.div initial="hidden" whileInView="show" viewport={{
          once: true
        }} variants={stagger} style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 14
        }}>
            {DASHBOARD_FEATURES.map((f, i) => <motion.div key={i} variants={fadeUp} whileHover={{
            x: 4
          }} style={{
            background: 'var(--surface-section-soft)',
            border: `1px solid ${BRAND_BORDER}`,
            borderRadius: 16,
            padding: '20px 22px',
            display: 'flex',
            alignItems: 'flex-start',
            gap: 16
          }}>
                <div style={{
              width: 42,
              height: 42,
              borderRadius: 11,
              background: BRAND_SUB,
              border: `1px solid ${BRAND_BORDER}`,
              display: 'grid',
              placeItems: 'center',
              flexShrink: 0
            }}>
                  <f.Icon size={18} color={BRAND} strokeWidth={2.2} />
                </div>
                <div>
                  <h3 style={{
                fontFamily: 'var(--font-geist), system-ui, sans-serif',
                fontWeight: 700,
                fontSize: 15,
                color: TXT_PRIMARY,
                marginBottom: 4
              }}>{t(f.title)}</h3>
                  <p style={{
                fontSize: 13.5,
                color: TXT_BODY,
                lineHeight: 1.6,
                margin: 0
              }}>{t(f.desc)}</p>
                </div>
              </motion.div>)}
          </motion.div>
        </div>
      </div>
    </section>;
}

/* ── PUBLISHING — 4-up card row ────────────────────────────────── */
function Publishing() {
  const {
    t
  } = useTranslation();
  return <section id="publishing" style={{
    padding: 'var(--section-pad)',
    background: 'var(--surface-section-soft)'
  }}>
      <div className="container">
        <SectionHeader pill="Publishing" title={t("solutions_for_marketing_teams.create_approve_publish_all_without")} sub="Your insights and your content workflow live in the same place. No more exporting data to one tool, then jumping to another to write the post." />

        <motion.div initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.15
      }} variants={stagger} style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(4, 1fr)',
        gap: 18
      }}>
          {PUBLISHING_FEATURES.map((f, i) => <motion.div key={i} variants={fadeUp} whileHover={{
          y: -4
        }} style={{
          background: 'var(--surface-card)',
          border: `1px solid ${BORDER_SOFT}`,
          borderRadius: 20,
          padding: 26
        }}>
              <div style={{
            width: 48,
            height: 48,
            borderRadius: 12,
            background: BRAND_SUB,
            border: `1px solid ${BRAND_BORDER}`,
            display: 'grid',
            placeItems: 'center',
            marginBottom: 18
          }}>
                <f.Icon size={22} color="var(--brand)" strokeWidth={2.2} />
              </div>
              <h3 style={{
            fontFamily: 'var(--font-geist), system-ui, sans-serif',
            fontWeight: 800,
            fontSize: 16,
            color: TXT_PRIMARY,
            marginBottom: 8
          }}>{t(f.title)}</h3>
              <p style={{
            fontSize: 13.5,
            color: TXT_BODY,
            lineHeight: 1.6,
            margin: 0
          }}>{t(f.desc)}</p>
            </motion.div>)}
        </motion.div>
      </div>
    </section>;
}

/* ── COMMAND CENTRE — left text + right live mock card ─────────── */
function CommandCentre() {
  const {
    t
  } = useTranslation();
  return <section id="command" style={{
    padding: 'var(--section-pad)',
    background: 'var(--surface-card)',
    position: 'relative',
    overflow: 'hidden'
  }}>
      <div aria-hidden style={{
      position: 'absolute',
      inset: 0,
      backgroundImage: 'radial-gradient(rgba(0, 152, 255,0.05) 1px, transparent 1px)',
      backgroundSize: '32px 32px',
      pointerEvents: 'none'
    }} />
      <div className="container" style={{
      position: 'relative'
    }}>
        <div style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1.05fr',
        gap: 64,
        alignItems: 'center'
      }}>
          {/* Left — text + features */}
          <motion.div initial="hidden" whileInView="show" viewport={{
          once: true
        }} variants={fadeUp}>
            <SectionPill>{t("plat.command")}</SectionPill>
            <h2 style={sectionTitleStyle}>{t("solutions_for_marketing_teams.the_war_room_your_marketing_leaders")}</h2>
            <p style={{
            fontSize: 17,
            color: TXT_BODY,
            lineHeight: 1.7,
            marginTop: 14,
            marginBottom: 32,
            maxWidth: 480
          }}>
              {t("solutions_for_marketing_teams.when_a_campaign_launches_a_crisis_b")}
            </p>

            <motion.div initial="hidden" whileInView="show" viewport={{
            once: true
          }} variants={stagger} style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 12
          }}>
              {COMMAND_FEATURES.map((f, i) => <motion.div key={i} variants={fadeUp} style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 14,
              padding: '16px 18px',
              background: 'var(--surface-section-soft)',
              border: `1px solid ${BORDER_SOFT}`,
              borderRadius: 14
            }}>
                  <div style={{
                width: 38,
                height: 38,
                borderRadius: 10,
                background: BRAND_SUB,
                border: `1px solid ${BRAND_BORDER}`,
                display: 'grid',
                placeItems: 'center',
                flexShrink: 0
              }}>
                    <f.Icon size={17} color="var(--brand)" strokeWidth={2.2} />
                  </div>
                  <div>
                    <h3 style={{
                  fontFamily: 'var(--font-geist), system-ui, sans-serif',
                  fontWeight: 700,
                  fontSize: 15,
                  color: TXT_PRIMARY,
                  marginBottom: 3
                }}>{t(f.title)}</h3>
                    <p style={{
                  fontSize: 13.5,
                  color: TXT_BODY,
                  lineHeight: 1.6,
                  margin: 0
                }}>{t(f.desc)}</p>
                  </div>
                </motion.div>)}
            </motion.div>
          </motion.div>

          {/* Right — live command card */}
          <motion.div initial={{
          opacity: 0,
          y: 40,
          scale: 0.96
        }} whileInView={{
          opacity: 1,
          y: 0,
          scale: 1
        }} viewport={{
          once: true
        }} transition={{
          type: 'spring',
          damping: 14,
          stiffness: 90,
          mass: 1
        }}>
            <div style={{
            background: BRAND_DEEP,
            border: '1px solid rgba(255,255,255,0.12)',
            borderRadius: 22,
            overflow: 'hidden'
          }}>
              <div style={{
              background: 'rgba(255,255,255,0.04)',
              borderBottom: '1px solid rgba(255,255,255,0.07)',
              padding: '12px 18px',
              display: 'flex',
              alignItems: 'center',
              gap: 10
            }}>
                <Tv size={14} color="rgba(255,255,255,0.4)" />
                <span style={{
                fontSize: 11,
                fontWeight: 700,
                color: 'rgba(255,255,255,0.65)',
                letterSpacing: '0.04em'
              }}>{t("solutions_for_marketing_teams.command_centre_live_view")}</span>
                <span style={{
                marginLeft: 'auto',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 5,
                fontSize: 10,
                fontWeight: 800,
                background: 'rgba(127,179,255,0.20)',
                color: 'var(--brand-sky)',
                padding: '3px 10px',
                borderRadius: 999,
                border: '1px solid rgba(127,179,255,0.30)'
              }}>
                  <span style={{
                  width: 5,
                  height: 5,
                  borderRadius: '50%',
                  background: 'var(--brand-sky)',
                  display: 'block',
                  animation: 'mktPulse 1.5s infinite'
                }} />
                  {t("solutions_for_marketing_teams.live")}
                </span>
              </div>

              <div style={{
              padding: 18,
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 12
            }}>
                {[{
                label: 'Share of Voice',
                val: '62.4%',
                delta: '↑ 8.2',
                pos: true
              }, {
                label: 'Brand Sentiment',
                val: '84',
                delta: '↑ 11',
                pos: true
              }, {
                label: 'Campaign ROI',
                val: '4.2x',
                delta: '↑ 0.7',
                pos: true
              }, {
                label: 'Competitor SOV',
                val: '31.6%',
                delta: '↓ 4.1',
                pos: false
              }].map((m, i) => <div key={i} style={{
                padding: '14px 16px',
                borderRadius: 12,
                background: 'rgba(255,255,255,0.04)',
                border: '1px solid rgba(255,255,255,0.08)'
              }}>
                    <div style={{
                  fontSize: 10,
                  fontWeight: 800,
                  color: 'rgba(255,255,255,0.42)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.1em',
                  marginBottom: 6
                }}>{t(m.label)}</div>
                    <div style={{
                  fontSize: 26,
                  fontWeight: 800,
                  color: 'var(--text-inverse)',
                  letterSpacing: '-0.03em',
                  lineHeight: 1
                }}>{m.val}</div>
                    <div style={{
                  fontSize: 11,
                  color: m.pos ? 'var(--brand-sky)' : '#FCA5A5',
                  marginTop: 4,
                  fontWeight: 700
                }}>{m.delta}</div>
                  </div>)}
              </div>

              <div style={{
              margin: '0 18px 18px',
              padding: '12px 14px',
              borderRadius: 12,
              background: 'linear-gradient(135deg, rgba(127,179,255,0.16), rgba(127,179,255,0.06))',
              border: '1px solid rgba(127,179,255,0.30)'
            }}>
                <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: 7,
                marginBottom: 5
              }}>
                  <span style={{
                  fontSize: 9.5,
                  fontWeight: 800,
                  background: 'var(--brand-sky)',
                  color: BRAND_DEEP,
                  padding: '2px 8px',
                  borderRadius: 4
                }}>{t("solutions_for_marketing_teams.signalsense")}</span>
                  <span style={{
                  fontSize: 11,
                  fontWeight: 700,
                  color: 'var(--brand-sky)'
                }}>{t("solutions_for_marketing_teams.viral_thread_detected")}</span>
                </div>
                <div style={{
                fontSize: 12,
                color: 'rgba(255,255,255,0.75)',
                lineHeight: 1.5
              }}>{t("solutions_for_marketing_teams.instagram_32k_mentions_in_2h_action")}</div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}


/* ── FAQ ───────────────────────────────────────────────────────── */
function FAQSection() {
  const {
    t
  } = useTranslation();
  return <section id="faq" style={{
    padding: 'var(--section-pad)',
    background: 'var(--surface-card)'
  }}>
      <div className="container">
        <SectionHeader pill="FAQ" title={t("solutions_for_marketing_teams.frequently_asked_questions")} sub="Everything marketing teams ask before going live." />

        <div style={{
        maxWidth: 820,
        margin: '0 auto',
        display: 'flex',
        flexDirection: 'column',
        gap: 12
      }}>
          <FaqAccordion items={FAQS.map(faq => ({ q: t(faq.q), a: t(faq.a) }))} />
        </div>
      </div>
    </section>;
}

/* ── CLOSING CTA ───────────────────────────────────────────────── */
function ClosingCTA() {
  const {
    t
  } = useTranslation();
  return <section style={{
    background: 'linear-gradient(135deg, #F4F8FF 0%, #EEF3FF 50%, #F8F9FF 100%)',
    position: 'relative',
    padding: 'var(--section-pad)',
    overflow: 'hidden'
  }}>
      <div aria-hidden style={{
      position: 'absolute',
      inset: 0,
      backgroundImage: 'radial-gradient(rgba(0, 152, 255,0.07) 1px, transparent 1px)',
      backgroundSize: '32px 32px',
      pointerEvents: 'none'
    }} />
      <div aria-hidden style={{
      position: 'absolute',
      width: 620,
      height: 620,
      borderRadius: '50%',
      top: -160,
      left: '50%',
      transform: 'translateX(-50%)',
      background: 'radial-gradient(circle, rgba(0, 152, 255,0.12) 0%, transparent 70%)',
      filter: 'blur(60px)',
      pointerEvents: 'none'
    }} />

      <div className="container" style={{
      position: 'relative',
      zIndex: 2,
      textAlign: 'center',
      maxWidth: 720,
      margin: '0 auto'
    }}>
        <motion.h2 initial={{
        opacity: 0,
        y: 24
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} style={{
        fontFamily: 'var(--font-geist), system-ui, sans-serif',
        fontSize: 'clamp(28px,4vw,46px)',
        fontWeight: 800,
        color: TXT_PRIMARY,
        letterSpacing: '-0.025em',
        marginBottom: 16,
        lineHeight: 1.08
      }}>
          {t("solutions_for_marketing_teams.see_your_outcome_model_in_30_minute")}
        </motion.h2>
        <motion.p initial={{
        opacity: 0,
        y: 16
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        delay: 0.1
      }} style={{
        fontSize: 17,
        color: TXT_BODY,
        lineHeight: 1.7,
        marginBottom: 12
      }}>
          {t("solutions_for_marketing_teams.a_tailored_walkthrough_for_your_ind")}
        </motion.p>
        <motion.p initial={{
        opacity: 0,
        y: 12
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        delay: 0.15
      }} style={{
        fontSize: 13,
        color: TXT_MUTED,
        marginBottom: 32
      }}>
          {t("solutions_for_marketing_teams.30min_personalised_walkthrough_no_c")}
        </motion.p>
        <motion.div initial={{
        opacity: 0,
        y: 16
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        delay: 0.2
      }} style={{
        display: 'flex',
        justifyContent: 'center',
        gap: 14,
        flexWrap: 'wrap'
      }}>
          <BookDemoButton label={t("hero.cta_book_demo")} />
        </motion.div>
      </div>
    </section>;
}

/* ── LOGO MARQUEE — infinite-scroll trusted-brand strip (new) ───── */
function LogoMarquee() {
  const { t } = useTranslation();
  const row = [...CLIENTS, ...CLIENTS]; // duplicated for seamless -50% loop
  return (
    <section style={{ padding: 'clamp(32px,4vh,52px) 0', background: 'var(--surface-card)', borderTop: '1px solid rgba(15,23,42,0.06)', borderBottom: '1px solid rgba(15,23,42,0.06)' }}>
      <div className="container" style={{ textAlign: 'center', marginBottom: 22 }}>
        <span style={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--text-muted)' }}>
          {t("solutions_for_marketing_teams.trusted_by_350_enterprise_brands") || 'Trusted by 350+ enterprise & government brands'}
        </span>
      </div>
      <div className="mkt-marquee" style={{ position: 'relative', overflow: 'hidden', WebkitMaskImage: 'linear-gradient(90deg, transparent, #000 8%, #000 92%, transparent)', maskImage: 'linear-gradient(90deg, transparent, #000 8%, #000 92%, transparent)' }}>
        <div className="mkt-marquee-track" style={{ display: 'flex', gap: 56, width: 'max-content', alignItems: 'center' }}>
          {row.map((c, i) => (
            <img key={i} src={c.src} alt={c.alt} loading="lazy" decoding="async" style={{ height: 30, width: 'auto', objectFit: 'contain', filter: 'grayscale(1)', opacity: 0.65 }} onError={e => { e.target.style.display = 'none'; }} />
          ))}
        </div>
      </div>
    </section>
  );
}

/* ── CountUp — SSR/crawl shows final value; animates 0 → target in view. */
function CountUp({ target, prefix = '', suffix = '', decimals = 0 }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.5 });
  const format = (v) => prefix + Number(v).toFixed(decimals) + suffix;
  const mv = useMotionValue(target);
  const [display, setDisplay] = useState(() => format(target));
  const started = useRef(false);
  useEffect(() => {
    if (!inView || started.current) return;
    started.current = true;
    mv.set(0);
    setDisplay(format(0));
    const controls = animate(mv, target, { duration: 1.6, ease: [0.22, 1, 0.36, 1] });
    const unsub = mv.on('change', (v) => setDisplay(format(v)));
    return () => { controls.stop(); unsub(); };
  }, [inView, target, prefix, suffix, decimals, mv]);
  return <span ref={ref}>{display}</span>;
}

/* ── RESULTS — count-up outcome band on tinted bg (new) ─────────── */
function ResultsCountUp() {
  const { t } = useTranslation();
  return (
    <section style={{ padding: 'var(--section-pad)', background: 'var(--surface-section-soft)' }}>
      <div className="container">
        <SectionHeader pill="Outcomes" title={t("solutions_for_marketing_teams.numbers_marketing_leaders_take_to_the_board") || 'Numbers marketing leaders take to the board'} sub="Real deployments. Real metrics. Measured across 350+ enterprise brands." />
        <motion.div initial="hidden" whileInView="show" viewport={viewportOnce} variants={staggerContainer(0.12)}
          style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 18, alignItems: 'stretch' }}>
          {RESULTS.map((r, i) => (
            <motion.div key={i} variants={mfadeUp} style={{
              background: 'var(--surface-card)', border: '1px solid rgba(15,23,42,0.08)', borderRadius: 20,
              padding: '30px 24px', textAlign: 'center',
              display: 'flex', flexDirection: 'column', alignItems: 'center',
            }}>
              <div style={{ fontSize: 'clamp(34px,3.6vw,48px)', fontWeight: 800, letterSpacing: '-0.03em', color: 'var(--brand)', lineHeight: 1 }}>
                <CountUp target={r.target} prefix={r.prefix} suffix={r.suffix} />
              </div>
              <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)', marginTop: 12, minHeight: 40, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{t(r.label)}</div>
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 'auto', paddingTop: 5 }}>{t(r.foot)}</div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}

/* ── TiltCard — 3D pointer-tilt wrapper (new hover animation) ───── */
function TiltCard({ children, style }) {
  const rx = useMotionValue(0);
  const ry = useMotionValue(0);
  const onMove = e => {
    const r = e.currentTarget.getBoundingClientRect();
    const px = (e.clientX - r.left) / r.width - 0.5;
    const py = (e.clientY - r.top) / r.height - 0.5;
    ry.set(px * 9);
    rx.set(-py * 9);
  };
  const reset = () => { rx.set(0); ry.set(0); };
  return (
    <motion.div onMouseMove={onMove} onMouseLeave={reset}
      style={{ rotateX: rx, rotateY: ry, transformPerspective: 800, transformStyle: 'preserve-3d', ...style }}
      transition={springSoft}
    >
      {children}
    </motion.div>
  );
}


/* ── Shared section header / pill primitives ───────────────────── */
function SectionPill({
  children
}) {
  const {
    t
  } = useTranslation();
  return <span style={{
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    background: BRAND_SUB,
    border: `1px solid ${BRAND_BORDER}`,
    padding: '6px 12px',
    borderRadius: 999,
    fontSize: 11,
    fontWeight: 800,
    color: BRAND,
    letterSpacing: '0.14em',
    textTransform: 'uppercase'
  }}>
      <Sparkles size={11} strokeWidth={2.5} />
      {children}
    </span>;
}
function SectionHeader({
  pill,
  title,
  sub
}) {
  const {
    t
  } = useTranslation();
  return <div style={{
    textAlign: 'center',
    marginBottom: 52,
    maxWidth: 720,
    marginLeft: 'auto',
    marginRight: 'auto'
  }}>
      <div style={{
      marginBottom: 16
    }}>
        <SectionPill>{pill}</SectionPill>
      </div>
      <h2 style={{
      ...sectionTitleStyle,
      textAlign: 'center'
    }}>{title}</h2>
      <p style={{
      fontSize: 16,
      color: TXT_BODY,
      lineHeight: 1.7,
      marginTop: 14
    }}>{sub}</p>
    </div>;
}
const sectionTitleStyle = {
  fontFamily: 'var(--font-geist), system-ui, sans-serif',
  fontSize: 'clamp(26px, 3.4vw, 40px)',
  fontWeight: 800,
  color: TXT_PRIMARY,
  letterSpacing: '-0.025em',
  lineHeight: 1.15,
  margin: 0
};